import type { WordsDictatedDailyPoint, WordsDictatedWeeklyPoint } from '@/api/teamInsights'

const MS_PER_DAY = 86_400_000

// Flat rate, no day-of-week seasonality — the dotted leg is straight by design.
const TRAILING_DAYS = 7

export interface UsageChartDatum extends WordsDictatedWeeklyPoint {
  // In-progress week only: desktop/mobile then hold the forecast, this the real total.
  actualSoFar?: number
  isProjected?: boolean
}

interface PlatformSplit {
  desktop: number
  mobile: number
}

// The marts bucket by UTC date, so every boundary below uses UTC getters.
const parseUtcDay = (iso: string): Date => new Date(`${iso}T00:00:00Z`)

const utcMidnight = (d: Date): Date => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))

const isoDay = (d: Date): string => d.toISOString().slice(0, 10)

const sundayOfWeek = (utcDay: Date): Date => new Date(utcDay.getTime() - utcDay.getUTCDay() * MS_PER_DAY)

// Today is excluded: a part-elapsed day under-projects. Divisor is the window, not the rows.
const trailingDailyRate = (daily: WordsDictatedDailyPoint[], today: Date): PlatformSplit => {
  const windowEnd = today.getTime() - MS_PER_DAY
  const windowStart = windowEnd - (TRAILING_DAYS - 1) * MS_PER_DAY
  const sum = { desktop: 0, mobile: 0 }
  for (const p of daily) {
    const t = parseUtcDay(p.period).getTime()
    if (t >= windowStart && t <= windowEnd) {
      sum.desktop += p.desktop
      sum.mobile += p.mobile
    }
  }
  return { desktop: sum.desktop / TRAILING_DAYS, mobile: sum.mobile / TRAILING_DAYS }
}

// Fractional on purpose: the unelapsed part of today is what gives Saturday a forecast.
const remainingWeekDays = (now: Date, weekStart: Date): number => {
  const today = utcMidnight(now)
  const weekEnd = weekStart.getTime() + 6 * MS_PER_DAY
  const wholeDaysLeft = Math.max(0, Math.round((weekEnd - today.getTime()) / MS_PER_DAY))
  const elapsedToday = (now.getTime() - today.getTime()) / MS_PER_DAY
  return wholeDaysLeft + Math.max(0, 1 - elapsedToday)
}

interface ProjectionOptions {
  now?: Date
  filterStart?: Date
  filterEnd?: Date
}

/** The in-progress week's actuals replaced by its forecast, so the chart shows no mid-week dip. */
export const withCurrentWeekProjection = (
  weekly: WordsDictatedWeeklyPoint[],
  daily: WordsDictatedDailyPoint[],
  { now = new Date(), filterStart, filterEnd }: ProjectionOptions = {}
): UsageChartDatum[] => {
  const actuals: UsageChartDatum[] = weekly.map((p) => ({ ...p }))

  const today = utcMidnight(now)
  if (filterEnd && filterEnd.getTime() < today.getTime()) return actuals

  const weekStart = sundayOfWeek(today)
  const rate = trailingDailyRate(daily, today)
  if (rate.desktop + rate.mobile <= 0) return actuals

  // A week with no dictations is absent from the series, so it may need appending.
  const currentIdx = actuals.findIndex((p) => p.period === isoDay(weekStart))
  const anchorIdx = currentIdx >= 0 ? currentIdx - 1 : actuals.length - 1
  if (anchorIdx < 0) return actuals

  // No zero buckets from the backend: an idle week is absent, so the leg would span the gap.
  const anchorPeriod = parseUtcDay(actuals[anchorIdx].period)
  if (anchorPeriod.getTime() !== weekStart.getTime() - 7 * MS_PER_DAY) return actuals

  // The first bucket is clipped by the range start — or on All time, by the earliest dictation.
  const seriesStart = filterStart ?? parseUtcDay(daily[0].period)
  if (seriesStart.getTime() > anchorPeriod.getTime()) return actuals

  const current = currentIdx >= 0 ? actuals[currentIdx] : undefined
  const soFar = { desktop: current?.desktop ?? 0, mobile: current?.mobile ?? 0 }
  const remaining = remainingWeekDays(now, weekStart)

  const projected: UsageChartDatum = {
    period: isoDay(weekStart),
    desktop: Math.round(soFar.desktop + rate.desktop * remaining),
    mobile: Math.round(soFar.mobile + rate.mobile * remaining),
    actualSoFar: soFar.desktop + soFar.mobile,
    isProjected: true,
  }
  if (currentIdx >= 0) actuals[currentIdx] = projected
  else actuals.push(projected)

  return actuals
}
